import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Crown, Sparkles, ChevronLeft, AlertTriangle, CalendarClock, ExternalLink, RotateCw } from "lucide-react";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { isAndroidNative, isIOSNative } from "@/lib/platform";
import { initIAP, purchaseIAP, restoreIAP, retryIAP, onIAPChange, onIAPStatus, getIAPProducts, getIAPStatus, IAP_CONSTANTS } from "@/lib/iap";

const FEATURES = [
  "Unlimited outfit ratings",
  "Unlimited AI outfit generation",
  "Unlimited Nova analyses",
  "Unlimited nutrition scans",
  "Unlimited fitness, skincare & fragrance coaching",
  "Priority response (no daily caps)",
  "Early access to new features",
];

const PRICE_DOLLARS = 4;
const PRICE_CENTS = 99;
const DAILY_COST = "Less than $0.17 per day";

function formatUnixDate(unix) {
  if (!unix) return null;
  try {
    const d = new Date(unix * 1000);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return null;
  }
}

export default function PremiumScreen() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const canceledRedirect = searchParams.get("canceled") === "1";

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sub, setSub] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [plans, setPlans] = useState([]);
  // Default to "yearly" so the Best-Value plan is preselected.
  const [selectedPlan, setSelectedPlan] = useState("yearly");

  // ---------------------------------------------------------------
  // Native Apple StoreKit IAP state. Only populated on iOS Capacitor.
  // - iapProducts.monthly / .yearly : summarised product info from StoreKit
  //   (localised priceString, period, etc.)
  // - iapStatus.phase = "initializing" | "loading" | "ready" | "error"
  //     Apple rejected Build 17 because we showed the "unavailable" copy
  //     while StoreKit was still fetching. Only phase === "error" is allowed
  //     to show that message — and only AFTER internal retries have failed.
  // - restoreBusy : disables the Restore button while it's in-flight
  // - infoMsg     : status text under the CTAs
  // ---------------------------------------------------------------
  const [iapProducts, setIapProducts] = useState({ monthly: null, yearly: null });
  const [iapStatus, setIapStatus]     = useState({ phase: "initializing", errorMessage: null, retryCount: 0 });
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [infoMsg, setInfoMsg] = useState("");

  const loadSubscription = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/subscription");
      setSub(data);
    } catch (err) {
      // non-fatal — show plan from the user object as fallback
      setSub({ premium: !!user?.premium, plan: user?.premium ? "glow_up_premium" : "free", stripe_enabled: true });
    }
  }, [user?.premium]);

  const loadPlans = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/plans");
      const list = data?.plans || [];
      setPlans(list);
      // If the preselected plan isn't available, fall back to the first that is.
      const sel = list.find((p) => p.id === selectedPlan && p.available);
      if (!sel) {
        const first = list.find((p) => p.available) || list[0];
        if (first) setSelectedPlan(first.id);
      }
    } catch {
      // Hard-coded fallback so the UI still renders if /billing/plans fails.
      setPlans([
        { id: "monthly", label: "Monthly", price_text: "$4.99", period_text: "/month", best_value: false, available: true, savings_text: null },
        { id: "yearly",  label: "Yearly",  price_text: "$39.99", period_text: "/year",  best_value: true,  available: false, savings_text: "Save 33% — that's like 4 months free" },
      ]);
    }
  }, [selectedPlan]);

  useEffect(() => { loadSubscription(); loadPlans(); }, [loadSubscription, loadPlans]);

  // Paywall view event — fires once when the screen mounts.
  useEffect(() => {
    try { track("paywall_viewed", { source_screen: document.referrer ? "internal" : "direct" }); } catch { /* ignore */ }
  }, []);

  // Stripe redirected back with ?canceled=1 → user bailed out at checkout.
  useEffect(() => {
    if (canceledRedirect) {
      try { track("purchase_failed", { provider: "stripe", error_code: "user_cancelled" }); } catch { /* ignore */ }
    }
  }, [canceledRedirect]);

  // ---------------------------------------------------------------
  // Native store IAP wiring. Runs inside iOS Capacitor (Apple StoreKit)
  // AND Android Capacitor (Google Play Billing). Same UI, same purchase
  // handler — the plugin routes to the correct native store.
  // Everything below is a no-op on the web build.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!isIOSNative() && !isAndroidNative()) return;
    let offProducts = () => {};
    let offStatus = () => {};
    setIapProducts(getIAPProducts());
    setIapStatus(getIAPStatus());
    initIAP(user?.id);
    offProducts = onIAPChange((p) => setIapProducts({ ...p }));
    offStatus   = onIAPStatus((s) => setIapStatus({ ...s }));
    return () => { offProducts(); offStatus(); };
  }, [user?.id]);

  const onManualRetry = async () => {
    setError(""); setInfoMsg("");
    await retryIAP();
  };

  const buyNative = async (which /* "monthly" | "yearly" */) => {
    const provider = isIOSNative() ? "apple" : "google";
    setBusy(true); setError(""); setInfoMsg("");
    try {
      try { track("checkout_started", { provider, plan_id: which }); } catch { /* ignore */ }
      await purchaseIAP(which);
      // The plugin's `.approved` handler POSTs to the correct
      // /billing/<store>/verify endpoint which flips premium on the
      // server. Poll /auth/me for up to ~10 seconds to reflect it.
      setInfoMsg("Processing your purchase…");
      for (let i = 0; i < 10; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        await refresh().catch(() => {});
        await loadSubscription().catch(() => {});
        // eslint-disable-next-line no-await-in-loop
        const me = await api.get("/auth/me").then((r) => r.data).catch(() => null);
        if (me?.premium) {
          setInfoMsg("Premium unlocked. Enjoy!");
          try { track("purchase_completed", { provider, plan_id: which }); } catch { /* ignore */ }
          break;
        }
      }
    } catch (err) {
      const msg = err?.message || "";
      if (/cancel|CANCELED|CANCELLED|USER_CANCEL/i.test(msg)) {
        try { track("purchase_failed", { provider, plan_id: which, error_code: "user_cancelled" }); } catch { /* ignore */ }
        setInfoMsg("");
      } else {
        try { track("purchase_failed", { provider, plan_id: which, error_code: "error" }); } catch { /* ignore */ }
        setError(msg || "Couldn't complete the purchase.");
      }
    } finally { setBusy(false); }
  };

  const doRestore = async () => {
    setRestoreBusy(true); setError(""); setInfoMsg("");
    try {
      await restoreIAP();
      // Give StoreKit's `.approved` / `.verified` callback a moment to hit our
      // backend, then re-read premium status.
      setInfoMsg("Restoring purchases…");
      for (let i = 0; i < 8; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        await refresh().catch(() => {});
        await loadSubscription().catch(() => {});
        // eslint-disable-next-line no-await-in-loop
        const me = await api.get("/auth/me").then((r) => r.data).catch(() => null);
        if (me?.premium) {
          setInfoMsg("Purchase restored. Premium is active.");
          break;
        }
      }
      // If premium never became true, the user simply has no active Apple
      // subscription — tell them so and clear the "restoring…" spinner text.
      const me2 = await api.get("/auth/me").then((r) => r.data).catch(() => null);
      if (!me2?.premium) setInfoMsg("No active subscriptions found for this Apple ID.");
    } catch (err) {
      setError(err?.message || "Couldn't restore purchases.");
    } finally { setRestoreBusy(false); }
  };

  const isPremium = !!sub?.premium;
  const willCancel = !!sub?.cancel_at_period_end;
  const periodEndStr = formatUnixDate(sub?.current_period_end);
  const activePlan = plans.find((p) => p.id === selectedPlan) || plans[0];

  // --- Checkout ----------------------------------------------------------
  const startCheckout = async () => {
    setBusy(true); setError("");
    try {
      try { track("checkout_started", { provider: "stripe", plan_id: selectedPlan }); } catch { /* ignore */ }
      const { data } = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
        plan: selectedPlan,
      });
      if (data?.checkout_url) {
        window.location.assign(data.checkout_url);
        return;
      }
      throw new Error("No checkout URL returned.");
    } catch (err) {
      try { track("purchase_failed", { provider: "stripe", plan_id: selectedPlan, error_code: err?.response?.status || "unknown" }); } catch { /* ignore */ }
      setError(formatApiError(err));
      setBusy(false);
    }
  };

  // --- Cancel ------------------------------------------------------------
  const cancelSubscription = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/billing/cancel");
      await loadSubscription();
      await refresh();
      setConfirmCancel(false);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  const resumeSubscription = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/billing/resume");
      await loadSubscription();
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  const openBillingPortal = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: window.location.origin + "/premium",
      });
      if (data?.portal_url) {
        window.location.assign(data.portal_url);
        return;
      }
      setError("Couldn't open billing portal. Try again.");
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="screen fade-in" data-testid="premium-screen">
      <div className="top-bar" style={{ marginBottom: 6 }}>
        <button className="icon-btn" aria-label="Back" onClick={() => navigate(-1)} data-testid="premium-back-btn">
          <ChevronLeft size={18} />
        </button>
        <div className="logo-mark">PREMIUM</div>
        <div style={{ width: 36 }} />
      </div>

      {canceledRedirect && !isPremium && (
        <div
          className="card"
          data-testid="premium-canceled-notice"
          style={{ padding: 14, marginBottom: 14, border: "1px solid var(--line)", background: "var(--bg-elev-2)" }}
        >
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <AlertTriangle size={16} color="var(--text-faint)" />
            <div className="body-sm" style={{ color: "var(--text)" }}>
              Checkout canceled — no payment taken. You&apos;re still on the free plan.
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>NOVA PREMIUM</div>
        <h2 className="h1" style={{ fontSize: 38 }}>
          The full <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>wardrobe</em>.
        </h2>
        <p className="body" style={{ marginTop: 12 }}>
          Unlimited analyses, scans, and coaching — every day. No daily caps. No hold-backs.
        </p>
      </div>

      {/* Pricing / status card */}
      <div
        className="card"
        style={{
          cursor: "default",
          padding: 0,
          background: "linear-gradient(180deg, #181818 0%, #0a0a0a 100%)",
          borderColor: "rgba(255,255,255,0.20)",
          overflow: "hidden",
        }}
        data-testid="premium-pricing-card"
      >
        <div
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "16px 22px", borderBottom: "1px solid var(--line)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Crown size={16} strokeWidth={1.7} />
            <div className="h3">Premium plan</div>
          </div>
          {activePlan?.best_value && (
            <span
              data-testid="premium-best-value-badge"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "5px 11px", borderRadius: 999,
                background: "#fff", color: "#000",
                fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600,
              }}
            >
              <Sparkles size={11} strokeWidth={2} /> Best Value
            </span>
          )}
        </div>

        {/* Monthly / Yearly toggle — defaults to yearly (Best Value) */}
        {!isPremium && plans.length > 0 && (
          <div
            style={{ padding: "16px 22px 8px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
            data-testid="premium-plan-toggle"
          >
            {plans.map((p) => {
              const active = selectedPlan === p.id;
              const disabled = !p.available;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { if (p.available) { setSelectedPlan(p.id); try { track("plan_selected", { plan_id: p.id, interval: p.interval }); } catch { /* ignore */ } } }}
                  disabled={disabled}
                  data-testid={`premium-plan-${p.id}`}
                  style={{
                    position: "relative",
                    padding: "14px 14px 12px",
                    borderRadius: 14,
                    background: active ? "rgba(232,211,185,0.14)" : "rgba(255,255,255,0.04)",
                    border: active ? "1px solid var(--gold)" : "1px solid rgba(255,255,255,0.10)",
                    color: "#fff",
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                    textAlign: "left",
                    transition: "all 160ms ease",
                  }}
                >
                  {p.best_value && (
                    <span
                      style={{
                        position: "absolute", top: -10, right: 12,
                        padding: "3px 8px", borderRadius: 999,
                        background: "#fff", color: "#000",
                        fontSize: 9, letterSpacing: "0.14em",
                        textTransform: "uppercase", fontWeight: 700,
                      }}
                    >
                      Best Value
                    </span>
                  )}
                  <div className="tiny" style={{ color: "rgba(255,255,255,0.55)", letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600 }}>
                    {p.label}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginTop: 8 }}>
                    <span className="serif numeric" style={{ fontSize: 26, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1 }}>
                      {p.price_text}
                    </span>
                    <span className="body-sm" style={{ color: "rgba(255,255,255,0.55)" }}>
                      {p.period_text}
                    </span>
                  </div>
                  {p.savings_text && (
                    <div className="tiny" style={{ marginTop: 6, color: "var(--gold)", lineHeight: 1.4 }}>
                      {p.savings_text}
                    </div>
                  )}
                  {disabled && (
                    <div className="tiny" style={{ marginTop: 6, color: "rgba(255,255,255,0.45)", lineHeight: 1.4 }}>
                      Coming soon
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Headline price for the currently selected plan */}
        {!isPremium && activePlan && (
          <div style={{ padding: "12px 22px 8px 22px" }} data-testid="premium-price">
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <span className="serif numeric" style={{ fontSize: 48, fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1 }}>
                {activePlan.price_text}
              </span>
              <span className="body" style={{ marginLeft: 6, color: "rgba(255,255,255,0.6)" }}>
                {activePlan.period_text}
              </span>
            </div>
            <div className="tiny numeric" style={{ marginTop: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)" }} data-testid="premium-daily-cost">
              {activePlan.id === "yearly"
                ? "About $3.33 per month — billed annually"
                : "Less than $0.17 per day"}
            </div>
          </div>
        )}

        {/* Premium users see their current plan card unchanged */}
        {isPremium && (
          <div style={{ padding: "22px 22px 8px 22px" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }} data-testid="premium-price">
              <span className="serif numeric" style={{ fontSize: 56, fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1 }}>
                ${PRICE_DOLLARS}
              </span>
              <span className="serif numeric" style={{ fontSize: 28, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1, color: "rgba(255,255,255,0.7)" }}>
                .{PRICE_CENTS}
              </span>
              <span className="body" style={{ marginLeft: 6, color: "rgba(255,255,255,0.6)" }}>/month</span>
            </div>
            <div className="tiny numeric" style={{ marginTop: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)" }} data-testid="premium-daily-cost">
              {DAILY_COST}
            </div>
          </div>
        )}

        <div className="hairline" style={{ margin: "14px 0" }} />

        <div style={{ padding: "4px 22px 22px 22px", display: "flex", flexDirection: "column", gap: 12 }} data-testid="premium-features">
          {FEATURES.map((f) => (
            <div
              key={f}
              style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 14, color: "#fff" }}
              data-testid={`premium-feature-${f.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
            >
              <Check size={16} strokeWidth={2} />
              <span>{f}</span>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div className="error-text" style={{ marginTop: 12 }} data-testid="premium-error">{error}</div>
      )}

      {/* Active subscription details */}
      {isPremium && (
        <div className="outfit-section" style={{ marginTop: 18 }} data-testid="premium-manage-section">
          <div className="eyebrow" style={{ marginBottom: 10 }}>YOUR SUBSCRIPTION</div>
          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <Crown size={16} strokeWidth={1.7} color="var(--gold)" />
              <div className="h3" style={{ color: "var(--text)" }}>Premium · Active</div>
            </div>
            <div className="body-sm" style={{ color: "var(--text-faint)" }} data-testid="premium-status-text">
              Status: {sub?.status || "active"}
            </div>
            {periodEndStr && (
              <div className="body-sm" style={{ marginTop: 6, color: "var(--text-faint)", display: "flex", alignItems: "center", gap: 6 }} data-testid="premium-next-billing">
                <CalendarClock size={13} />
                {willCancel
                  ? <>Premium ends on <strong style={{ color: "var(--text)" }}>{periodEndStr}</strong></>
                  : <>Next billing on <strong style={{ color: "var(--text)" }}>{periodEndStr}</strong></>}
              </div>
            )}
          </div>

          {!willCancel ? (
            !confirmCancel ? (
              <>
                {/* Native store management (iOS App Store or Google Play).
                    Neither store permits external cancel links per policy —
                    users must manage from their native subscription centre. */}
                {(isIOSNative() || isAndroidNative()) ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        if (isIOSNative()) {
                          window.location.href = "itms-apps://apps.apple.com/account/subscriptions";
                        } else {
                          // Google Play deep-link. `sku` is optional but
                          // helpful — if the user has multiple products
                          // it drops them right on the correct one.
                          window.location.href = "https://play.google.com/store/account/subscriptions?package=com.ixnova.app";
                        }
                      }}
                      data-testid={isIOSNative() ? "premium-ios-manage-subscription-btn" : "premium-android-manage-subscription-btn"}
                      style={{ width: "100%", justifyContent: "center", padding: "14px 18px" }}
                    >
                      Manage Subscription
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={doRestore}
                      disabled={restoreBusy}
                      data-testid={isIOSNative() ? "premium-ios-restore-btn-active" : "premium-android-restore-btn-active"}
                      style={{ width: "100%", justifyContent: "center", padding: "12px 16px" }}
                    >
                      {restoreBusy ? <span className="spinner" /> : <><RotateCw size={13} /> Restore Purchases</>}
                    </button>
                    <div className="tiny" style={{ textAlign: "center", letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--text-faint)", marginTop: 2 }}>
                      {isIOSNative() ? "Managed through your Apple ID" : "Managed through your Google Play account"}
                    </div>
                  </div>
                ) : (
                  <button
                    className="btn btn-outline"
                    onClick={openBillingPortal}
                    disabled={busy}
                    data-testid="premium-billing-portal-btn"
                    style={{ marginTop: 12, width: "100%", justifyContent: "center" }}
                  >
                    {busy ? <span className="spinner" /> : "Manage billing & invoices"}
                  </button>
                )}
                {/* Cancel-subscription button hidden on ALL native stores —
                    cancellation happens where the subscription was created. */}
                {!isIOSNative() && !isAndroidNative() && (
                  <button
                    className="btn btn-outline"
                    onClick={() => setConfirmCancel(true)}
                    disabled={busy}
                    data-testid="premium-cancel-btn"
                    style={{ marginTop: 10, width: "100%", justifyContent: "center" }}
                  >
                    Cancel subscription
                  </button>
                )}
              </>
            ) : (
              <div className="card" style={{ padding: 14, marginTop: 12, border: "1px solid var(--danger-line, rgba(255,120,120,0.32))" }}>
                <div className="body-sm" style={{ marginBottom: 10, color: "var(--text)" }}>
                  You&apos;ll keep Premium until {periodEndStr || "the end of the current period"}, then automatically switch to the free plan. You can resume anytime before that.
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    className="btn btn-ghost"
                    onClick={() => setConfirmCancel(false)}
                    disabled={busy}
                    data-testid="premium-cancel-keep-btn"
                    style={{ flex: 1, justifyContent: "center" }}
                  >
                    Keep Premium
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={cancelSubscription}
                    disabled={busy}
                    data-testid="premium-cancel-confirm-btn"
                    style={{ flex: 1, justifyContent: "center", background: "var(--danger, #c84a4a)", color: "#fff" }}
                  >
                    {busy ? <span className="spinner" /> : "Yes, cancel"}
                  </button>
                </div>
              </div>
            )
          ) : (
            isIOSNative() ? (
              <div
                data-testid="premium-ios-readonly-resume"
                style={{
                  marginTop: 12,
                  padding: 14,
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.03)",
                  color: "var(--text-dim)",
                  fontSize: 13,
                  lineHeight: 1.55,
                }}
              >
                Your subscription is set to end on {periodEndStr || "the end of the current period"}. To resume, sign in on any web browser using the account you subscribed on.
              </div>
            ) : (
              <button
                className="btn btn-outline"
                onClick={resumeSubscription}
                disabled={busy}
                data-testid="premium-resume-btn"
                style={{ marginTop: 12, width: "100%", justifyContent: "center" }}
              >
                {busy ? <span className="spinner" /> : "Resume subscription"}
              </button>
            )
          )}
        </div>
      )}

      {/* Web + non-native builds — Stripe checkout stays. */}
      {!isPremium && !isAndroidNative() && !isIOSNative() && (
        <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            className="btn btn-primary"
            onClick={startCheckout}
            disabled={busy || !sub?.stripe_enabled}
            data-testid="premium-upgrade-btn"
          >
            {busy ? <span className="spinner" /> : <>Go Premium — ${PRICE_DOLLARS}.{PRICE_CENTS}/mo</>}
          </button>
          {!sub?.stripe_enabled && (
            <div className="tiny" style={{ textAlign: "center", color: "var(--text-faint)" }}>
              Payments temporarily unavailable. Try again shortly.
            </div>
          )}
          <div className="tiny" style={{ textAlign: "center", letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--text-faint)" }}>
            Secure checkout via Stripe · Cancel anytime
          </div>
        </div>
      )}

      {/* Android (Play Store) build: native Google Play Billing is used
          instead of Stripe. The unified IAP block below handles both. */}

      {/* Native In-App Purchase — iOS via StoreKit, Android via Google Play
          Billing. Same code path, same UI, same backend verify flow. */}
      {!isPremium && (isIOSNative() || isAndroidNative()) && (
        <div style={{ marginTop: 18 }} data-testid="premium-native-iap-block">
          {(iapStatus.phase === "initializing" || iapStatus.phase === "loading") && (
            <div
              data-testid="premium-native-loading"
              style={{ padding: 22, textAlign: "center", color: "var(--text-faint)", fontSize: 13, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}
            >
              <span className="spinner" aria-label="Loading" />
              <div>Loading subscription options from {isIOSNative() ? "the App Store" : "Google Play"}…</div>
              <div style={{ fontSize: 11, color: "var(--text-faint)", opacity: 0.7 }}>
                This can take a few seconds on the first launch.
              </div>
            </div>
          )}

          {iapStatus.phase === "ready" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {iapProducts.yearly && (
                <button
                  type="button"
                  onClick={() => buyNative("yearly")}
                  disabled={busy}
                  className="btn btn-primary"
                  data-testid="premium-native-buy-yearly-btn"
                  style={{ justifyContent: "center", padding: "14px 18px" }}
                >
                  {busy ? <span className="spinner" /> : (
                    <>
                      Yearly — {iapProducts.yearly.priceString}
                      <span style={{ marginLeft: 8, opacity: 0.7, fontWeight: 400 }}>· Best value</span>
                    </>
                  )}
                </button>
              )}
              {iapProducts.monthly && (
                <button
                  type="button"
                  onClick={() => buyNative("monthly")}
                  disabled={busy}
                  className="btn btn-outline"
                  data-testid="premium-native-buy-monthly-btn"
                  style={{ justifyContent: "center", padding: "14px 18px" }}
                >
                  {busy ? <span className="spinner" /> : (
                    <>Monthly — {iapProducts.monthly.priceString}</>
                  )}
                </button>
              )}
              <div className="tiny" style={{ textAlign: "center", letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--text-faint)", marginTop: 4 }}>
                {isIOSNative() ? "Cancel anytime in iOS Settings" : "Cancel anytime in Google Play"}
              </div>
            </div>
          )}

          {iapStatus.phase === "error" && (
            <div
              data-testid="premium-native-unavailable"
              style={{
                marginTop: 4, padding: 18, borderRadius: 14,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.03)",
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)", marginBottom: 6 }}>
                About IX Nova Premium
              </div>
              <p style={{ fontSize: 13, lineHeight: 1.65, color: "var(--text-dim)", margin: 0 }}>
                Unable to load subscription options. Please try again.
              </p>
              <button
                type="button"
                onClick={onManualRetry}
                data-testid="premium-native-retry-btn"
                className="btn btn-outline"
                style={{ marginTop: 12, width: "100%", justifyContent: "center", padding: "12px 16px" }}
              >
                <RotateCw size={14} /> Retry
              </button>
            </div>
          )}

          {/* Restore Purchases — required by Apple 3.1.1 and standard on
              Google Play (users switching devices need to restore their sub). */}
          <button
            type="button"
            onClick={doRestore}
            disabled={restoreBusy}
            data-testid="premium-native-restore-btn"
            style={{
              marginTop: 14, width: "100%", padding: "12px 14px",
              background: "transparent", border: "1px solid rgba(255,255,255,0.10)",
              color: "var(--text-dim)", borderRadius: 999, cursor: "pointer",
              fontSize: 12.5, fontWeight: 500, letterSpacing: "0.06em",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            {restoreBusy ? <span className="spinner" /> : <><RotateCw size={13} /> Restore Purchases</>}
          </button>

          {infoMsg && (
            <div data-testid="premium-native-info-msg" style={{ marginTop: 10, textAlign: "center", color: "var(--gold, #b28a3e)", fontSize: 12.5 }}>
              {infoMsg}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
